import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import {
  Breadcrumbs, Container, H2, H3, PageHero, Section,
} from "../components/Primitives";
import { TOOLS_NAV } from "../content/nav";
import { useSEO } from "../lib/seo";
import { Stagger, StaggerItem } from "../lib/motion";

export default function Tools() {
  useSEO({
    title: "Free Tools — Verify a Certificate, Rudraksha Finder, Kundli & Muhurat",
    description:
      "Free tools that work whether or not you buy anything, including a certificate verifier that works on any seller's report number and a rudraksha finder that can tell you that you need nothing.",
    canonical: "/tools",
  });

  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Tools" }]} />
      </Container>

      <PageHero
        eyebrow="Free tools"
        lines={["Useful whether or not", "you buy anything"]}
        lede="No email gate before a result. No tool that only ever recommends something. The verifier works on report numbers from any seller, including the one trying to sell you something right now."
      />

      <Section>
        <Container>
          <Stagger className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" stagger={0.07}>
            <StaggerItem className="lg:col-span-3">
              <Link
                to="/tools/verify-a-certificate"
                data-testid="tools-verify-tile"
                className="group flex flex-col justify-between gap-8 border-2 border-verify bg-verify/[0.05] p-8 transition-colors duration-300 hover:bg-verify/[0.11] lg:flex-row lg:items-end lg:p-12"
              >
                <div>
                  <p className="eyebrow !text-verify">The strategic one</p>
                  <H2 className="mt-4 max-w-3xl">
                    Verify any gemstone certificate — including one you didn't buy from us
                  </H2>
                  <p className="ui measure mt-5 text-sm leading-[1.7] text-dim">
                    Choose the laboratory, enter the report number, and we'll take you to that laboratory's
                    own verification page. We don't hold a copy. We can't edit it. That's the entire point.
                  </p>
                </div>
                <span className="link-u ui inline-flex shrink-0 items-center gap-1.5 self-start text-sm lg:self-end">
                  Open the tool
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </span>
              </Link>
            </StaggerItem>

            {TOOLS_NAV.filter((t) => !t.to.includes("verify")).map((t) => (
              <StaggerItem key={t.to}>
                <Link
                  to={t.to}
                  data-testid={`tools-tile-${t.to.split("/").pop()}`}
                  className="card-s elev group flex h-full flex-col p-7 transition-colors duration-300 hover:border-gold"
                >
                  <H3 className="transition-colors group-hover:text-gold">{t.label}</H3>
                  <p className="ui mt-2.5 flex-1 text-sm leading-[1.7] text-dim">{t.note}</p>
                  <ArrowUpRight className="mt-6 h-4 w-4 text-dim transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold" />
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </Container>
      </Section>
    </>
  );
}
